/**
 * src/components/pr-testing/PRTestBar.jsx
 *
 * Floating PR testing bar — same pattern as BetaTestBar.
 * Collapsed: orange "PR" tab on the right edge of the screen.
 * Expanded: sidebar panel listing merged PRs with approve + auto-parsed
 * manual steps from the PR body (## Manual steps section).
 *
 * Only rendered in dev/test environments (controlled by App.jsx).
 */
import { useState, useCallback } from 'react';
import { usePRTesting } from '../../hooks/usePRTesting.js';

// ── Time helper ──────────────────────────────────────────────

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const days = Math.floor(diff / 86400000);
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days}d ago`;
  const weeks = Math.floor(days / 7);
  if (weeks < 8) return `${weeks}w ago`;
  return new Date(dateStr).toLocaleDateString();
}

// ── Step checkbox ────────────────────────────────────────────

function StepCheckbox({ text, checked, onToggle }) {
  return (
    <button
      onClick={onToggle}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 8,
        padding: '4px 0',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        textAlign: 'left',
        width: '100%',
      }}
    >
      <span
        style={{
          width: 16,
          height: 16,
          borderRadius: 4,
          flexShrink: 0,
          marginTop: 1,
          border: `1px solid ${checked ? 'color-mix(in srgb, var(--accent2) 40%, transparent)' : 'var(--border)'}`,
          background: checked
            ? 'color-mix(in srgb, var(--accent2) 15%, transparent)'
            : 'transparent',
          color: checked ? 'var(--accent2)' : 'transparent',
          fontSize: 10,
          lineHeight: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all 0.15s',
        }}
      >
        {checked ? '✓' : ''}
      </span>
      <span
        style={{
          fontSize: 11,
          lineHeight: 1.4,
          color: checked ? 'var(--fg3)' : 'var(--fg)',
          textDecoration: checked ? 'line-through' : 'none',
          opacity: checked ? 0.6 : 1,
          transition: 'all 0.15s',
        }}
      >
        <StepText text={text} />
      </span>
    </button>
  );
}

// ── Env badge ────────────────────────────────────────────────

const ENV_STYLES = {
  development: { label: 'DEVELOPMENT', color: 'var(--medium)', bg: 'var(--medium-bg)' },
  test: { label: 'TEST', color: 'var(--warn)', bg: 'var(--warn-bg)' },
  production: { label: 'PRODUCTION', color: 'var(--ok)', bg: 'var(--ok-bg)' },
  unknown: { label: 'ENV ?', color: 'var(--neutral)', bg: 'var(--neutral-bg)' },
};

function EnvBadge({ env }) {
  const style = ENV_STYLES[env] || ENV_STYLES.development;
  return (
    <span
      style={{
        padding: '1px 6px',
        borderRadius: 4,
        background: style.bg,
        color: style.color,
        fontSize: 8,
        fontWeight: 800,
        fontFamily: 'var(--font-mono)',
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        flexShrink: 0,
      }}
    >
      {style.label}
    </span>
  );
}

// ── Category badges ──────────────────────────────────────────

const CATEGORY_DEFS = [
  { key: 'sql', label: 'SQL', color: 'var(--cat-sky)', bg: 'var(--cat-sky-bg)' },
  { key: 'azure', label: 'AZURE', color: 'var(--cat-violet)', bg: 'var(--cat-violet-bg)' },
  { key: 'cloudflare', label: 'CLOUDFLARE', color: 'var(--cat-amber)', bg: 'var(--cat-amber-bg)' },
  { key: 'other', label: 'MANUAL', color: 'var(--neutral)', bg: 'var(--neutral-bg)' },
];

function CategoryBadges({ categories }) {
  const active = CATEGORY_DEFS.filter((d) => categories[d.key]);
  if (active.length === 0) return null;
  return (
    <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>
      {active.map((d) => (
        <span
          key={d.key}
          style={{
            padding: '1px 5px',
            borderRadius: 4,
            background: d.bg,
            color: d.color,
            fontSize: 8,
            fontWeight: 800,
            fontFamily: 'var(--font-mono)',
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            flexShrink: 0,
          }}
        >
          {d.label}
        </span>
      ))}
    </span>
  );
}

// ── SQL file links ──────────────────────────────────────────

const GITHUB_BLOB = 'https://github.com/ecurity-msk/seasuite/blob/development/';

function SqlFileLinks({ files }) {
  if (!files || files.length === 0) return null;
  return (
    <div style={{ marginTop: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
      {files.map((f) => (
        <a
          key={f}
          href={`${GITHUB_BLOB}${f}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            fontSize: 10,
            fontFamily: 'var(--font-mono)',
            color: 'var(--cat-sky)',
            textDecoration: 'none',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            opacity: 0.85,
            transition: 'opacity 0.15s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.opacity = '1';
            e.currentTarget.style.textDecoration = 'underline';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.opacity = '0.85';
            e.currentTarget.style.textDecoration = 'none';
          }}
        >
          <span style={{ fontSize: 9, opacity: 0.7 }}>&#128451;</span>
          {f}
          <span style={{ fontSize: 9, opacity: 0.5 }}>↗</span>
        </a>
      ))}
    </div>
  );
}

// ── Step text with linkified file paths ─────────────────────

const FILE_PATH_RE = /(`[^`]*\w[\w.-]*\.sql`|(?:[\w./-]+\/)\w[\w.-]*\.sql)\b/g;

function StepText({ text }) {
  const parts = [];
  let lastIdx = 0;
  let m;
  const re = new RegExp(FILE_PATH_RE.source, 'g');
  while ((m = re.exec(text)) !== null) {
    if (m.index > lastIdx) parts.push(text.slice(lastIdx, m.index));
    const raw = m[0].replace(/^`|`$/g, '');
    parts.push(
      <a
        key={m.index}
        href={`${GITHUB_BLOB}${raw}`}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          color: 'var(--cat-sky)',
          textDecoration: 'underline',
          textDecorationColor: 'color-mix(in srgb, var(--cat-sky) 30%, transparent)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {raw}
      </a>
    );
    lastIdx = m.index + m[0].length;
  }
  if (lastIdx < text.length) parts.push(text.slice(lastIdx));
  return parts.length > 1 ? <>{parts}</> : <>{text}</>;
}

// ── Per-PR "How to test" accordion ──────────────────────────

function HowToTestAccordion({ steps }) {
  const [open, setOpen] = useState(false);
  if (steps.length === 0) return null;
  return (
    <div style={{ marginTop: 8, borderTop: '1px solid var(--border)' }}>
      <button
        onClick={() => setOpen((v) => !v)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '5px 0 4px',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        <span
          style={{
            fontSize: 9,
            fontWeight: 700,
            color: 'var(--medium)',
            fontFamily: 'var(--font-mono)',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
          }}
        >
          How to test this PR
        </span>
        <span
          style={{
            fontSize: 9,
            color: 'var(--fg3)',
            transition: 'transform 0.2s',
            display: 'inline-block',
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
        >
          &#9662;
        </span>
      </button>
      {open && (
        <div style={{ paddingBottom: 6, display: 'flex', flexDirection: 'column', gap: 5 }}>
          {steps.map((text, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <span
                style={{
                  flexShrink: 0,
                  width: 16,
                  height: 16,
                  borderRadius: '50%',
                  background: 'color-mix(in srgb, var(--medium) 12%, transparent)',
                  border: '1px solid color-mix(in srgb, var(--medium) 25%, transparent)',
                  color: 'var(--medium)',
                  fontSize: 8,
                  fontWeight: 800,
                  fontFamily: 'var(--font-mono)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {i + 1}
              </span>
              <span style={{ fontSize: 11, color: 'var(--fg2)', lineHeight: 1.5, paddingTop: 1 }}>
                {text}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── PR row ───────────────────────────────────────────────────

function PRRow({
  pr,
  steps,
  howToTestSteps,
  categories,
  isStepChecked,
  onToggleStep,
  onApprove,
  onClaudeFix,
}) {
  const allStepsDone = steps.length > 0 && steps.every((s) => isStepChecked(pr.number, s));
  const [fixCopied, setFixCopied] = useState(false);

  const handleClaudeFix = useCallback(() => {
    const stepLines =
      steps.length > 0
        ? '\n\nManual steps in this PR:\n' + steps.map((s) => `- ${s}`).join('\n')
        : '';
    const text = [
      `I need help with changes from PR #${pr.number} — ${pr.title}`,
      '',
      `PR Info:`,
      `- Number: #${pr.number}`,
      `- Author: ${pr.user?.login ?? 'unknown'}`,
      `- URL: ${pr.html_url}`,
      '',
      `PR Description:`,
      pr.body?.trim() || '(no description)',
      stepLines,
    ].join('\n');

    navigator.clipboard.writeText(text).then(() => {
      setFixCopied(true);
      setTimeout(() => setFixCopied(false), 2000);
    });
    onClaudeFix?.(pr.number);
  }, [pr, steps, onClaudeFix]);

  return (
    <div
      style={{
        padding: '12px 14px',
        borderBottom: '1px solid var(--border)',
      }}
    >
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        <span
          style={{
            flexShrink: 0,
            minWidth: 36,
            padding: '2px 6px',
            borderRadius: 5,
            background: 'var(--bg3)',
            border: '1px solid var(--border)',
            fontSize: 10,
            fontWeight: 700,
            color: 'var(--fg3)',
            fontFamily: 'var(--font-mono)',
            textAlign: 'center',
          }}
        >
          #{pr.number}
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12, color: 'var(--fg)', lineHeight: 1.5 }}>{pr.title}</div>
          <div
            style={{
              fontSize: 10,
              color: 'var(--fg3)',
              fontFamily: 'var(--font-mono)',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              marginTop: 3,
              flexWrap: 'wrap',
            }}
          >
            <span>{pr.user?.login}</span>
            <span style={{ opacity: 0.4 }}>·</span>
            <span>merged {timeAgo(pr.merged_at)}</span>
            <a
              href={pr.html_url}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: 'var(--fg3)',
                textDecoration: 'none',
                opacity: 0.5,
                transition: 'opacity 0.15s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.opacity = '1';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.opacity = '0.5';
              }}
            >
              ↗
            </a>
            <EnvBadge env={pr.env} />
            <CategoryBadges categories={categories} />
          </div>

          {/* SQL file quick-links */}
          {categories.sqlFiles?.length > 0 && <SqlFileLinks files={categories.sqlFiles} />}

          {/* Auto-parsed manual steps */}
          {steps.length > 0 && (
            <div
              style={{ marginTop: 8, padding: '6px 0 2px', borderTop: '1px solid var(--border)' }}
            >
              <div
                style={{
                  fontSize: 9,
                  fontWeight: 700,
                  color: 'var(--fg3)',
                  fontFamily: 'var(--font-mono)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  marginBottom: 4,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                Manual steps
                {allStepsDone && (
                  <span style={{ color: 'var(--accent2)', fontSize: 8, fontWeight: 800 }}>
                    ALL DONE
                  </span>
                )}
              </div>
              {steps.map((text, i) => (
                <StepCheckbox
                  key={i}
                  text={text}
                  checked={isStepChecked(pr.number, text)}
                  onToggle={() => onToggleStep(pr.number, text)}
                />
              ))}
            </div>
          )}

          {/* How to test this PR */}
          <HowToTestAccordion steps={howToTestSteps} />

          {/* Action buttons */}
          <div style={{ marginTop: 8, display: 'flex', gap: 6 }}>
            <button
              onClick={() => onApprove(pr.number)}
              style={{
                padding: '5px 14px',
                borderRadius: 6,
                border: 'none',
                background: 'color-mix(in srgb, var(--accent2) 12%, transparent)',
                color: 'var(--accent2)',
                fontSize: 11,
                fontWeight: 700,
                cursor: 'pointer',
                fontFamily: 'var(--font-mono)',
                transition: 'all 0.15s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background =
                  'color-mix(in srgb, var(--accent2) 22%, transparent)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background =
                  'color-mix(in srgb, var(--accent2) 12%, transparent)';
              }}
            >
              ✓ Tested
            </button>
            <button
              onClick={handleClaudeFix}
              title="Copy PR context to clipboard for a new Claude session"
              style={{
                padding: '5px 14px',
                borderRadius: 6,
                border: 'none',
                background: fixCopied
                  ? 'color-mix(in srgb, var(--cat-violet) 22%, transparent)'
                  : 'color-mix(in srgb, var(--cat-violet) 10%, transparent)',
                color: 'var(--cat-violet)',
                fontSize: 11,
                fontWeight: 700,
                cursor: 'pointer',
                fontFamily: 'var(--font-mono)',
                transition: 'all 0.15s',
              }}
              onMouseEnter={(e) => {
                if (!fixCopied)
                  e.currentTarget.style.background =
                    'color-mix(in srgb, var(--cat-violet) 20%, transparent)';
              }}
              onMouseLeave={(e) => {
                if (!fixCopied)
                  e.currentTarget.style.background =
                    'color-mix(in srgb, var(--cat-violet) 10%, transparent)';
              }}
            >
              {fixCopied ? '✓ Copied!' : '⧉ Claude Fixing'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── How to test guide ────────────────────────────────────────

const HOW_TO_STEPS = [
  { n: 1, text: 'Open the PR link (↗) and read the description to understand what changed.' },
  {
    n: 2,
    text: 'If the PR has a SQL badge, run the linked migration script on the dev database before testing.',
  },
  {
    n: 3,
    text: 'If there are Manual steps listed, work through each checkbox top-to-bottom. Every step must pass.',
  },
  { n: 4, text: 'Test the golden path first — the primary user action the PR was built for.' },
  { n: 5, text: 'Test at least one edge case (empty state, missing data, wrong role, etc.).' },
  {
    n: 6,
    text: 'Check that nothing obviously broke in adjacent areas (dashboard, navigation, related module).',
  },
  {
    n: 7,
    text: 'If something is wrong, use "⧉ Claude Fixing" to copy full PR context to clipboard, then paste into a new Claude session.',
  },
  {
    n: 8,
    text: 'Once you are confident the PR works correctly, click "✓ Tested" to mark it done.',
  },
];

function HowToGuide() {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderBottom: '1px solid var(--border)' }}>
      <button
        onClick={() => setOpen((v) => !v)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '9px 18px',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          textAlign: 'left',
          borderBottom: open ? '1px solid var(--border)' : 'none',
        }}
      >
        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: 'var(--fg3)',
            fontFamily: 'var(--font-mono)',
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
          }}
        >
          How to test
        </span>
        <span
          style={{
            fontSize: 10,
            color: 'var(--fg3)',
            transition: 'transform 0.2s',
            display: 'inline-block',
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
        >
          ▾
        </span>
      </button>
      {open && (
        <div style={{ padding: '8px 18px 12px', display: 'flex', flexDirection: 'column', gap: 7 }}>
          {HOW_TO_STEPS.map((s) => (
            <div key={s.n} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
              <span
                style={{
                  flexShrink: 0,
                  width: 18,
                  height: 18,
                  borderRadius: '50%',
                  background: 'color-mix(in srgb, var(--medium) 15%, transparent)',
                  border: '1px solid color-mix(in srgb, var(--medium) 30%, transparent)',
                  color: 'var(--medium)',
                  fontSize: 9,
                  fontWeight: 800,
                  fontFamily: 'var(--font-mono)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {s.n}
              </span>
              <span style={{ fontSize: 11, color: 'var(--fg2)', lineHeight: 1.5, paddingTop: 1 }}>
                {s.text}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main floating bar ────────────────────────────────────────

export default function PRTestBar() {
  const {
    untestedPRs,
    loading,
    error,
    approve,
    toggleStep,
    isStepChecked,
    getStepsForPR,
    getHowToTestForPR,
    getCategoriesForPR,
    prTestBarEnabled,
    refresh,
  } = usePRTesting();
  const [expanded, setExpanded] = useState(false);

  // Hidden by super-admin toggle
  if (!prTestBarEnabled) return null;

  // ── Collapsed tab ──────────────────────────────────────────

  if (!expanded) {
    return (
      <button
        onClick={() => setExpanded(true)}
        className={untestedPRs.length > 0 ? 'pr-blink-tab' : undefined}
        style={{
          position: 'fixed',
          right: 0,
          top: '65%',
          transform: 'translateY(-50%)',
          zIndex: 9999,
          padding: '12px 8px',
          borderRadius: '8px 0 0 8px',
          background: 'linear-gradient(135deg, var(--medium-strong), var(--medium))',
          border: '1px solid color-mix(in srgb, var(--medium) 40%, transparent)',
          borderRight: 'none',
          color: 'var(--ink-on-color)',
          fontWeight: 800,
          fontSize: 11,
          fontFamily: 'var(--font-mono)',
          cursor: 'pointer',
          letterSpacing: '0.1em',
          writingMode: 'vertical-lr',
          textOrientation: 'mixed',
          boxShadow: '0 4px 20px rgba(234,88,12,0.4)',
        }}
      >
        PR{untestedPRs.length > 0 ? ` ${untestedPRs.length}` : ''}
      </button>
    );
  }

  // ── Expanded bar ───────────────────────────────────────────

  return (
    <div
      style={{
        position: 'fixed',
        right: 0,
        top: 0,
        bottom: 0,
        width: 380,
        zIndex: 9998,
        background: 'var(--bg2)',
        borderLeft: '1px solid var(--border)',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '-4px 0 30px rgba(0,0,0,0.4)',
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '16px 18px',
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span
            style={{
              padding: '3px 10px',
              borderRadius: 6,
              background: 'linear-gradient(135deg, var(--medium-strong), var(--medium))',
              color: 'var(--ink-on-color)',
              fontSize: 10,
              fontWeight: 800,
              fontFamily: 'var(--font-mono)',
              letterSpacing: '0.12em',
            }}
          >
            PR
          </span>
          <span className="display" style={{ fontSize: 14, fontWeight: 700, color: 'var(--fg)' }}>
            Testing Tracker
          </span>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button
            onClick={() => refresh()}
            title="Refresh PRs"
            style={{
              width: 28,
              height: 28,
              borderRadius: 6,
              border: '1px solid var(--border)',
              background: 'transparent',
              color: 'var(--fg3)',
              fontSize: 14,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.15s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor =
                'color-mix(in srgb, var(--medium) 40%, transparent)';
              e.currentTarget.style.color = 'var(--medium)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'var(--border)';
              e.currentTarget.style.color = 'var(--fg3)';
            }}
          >
            ↻
          </button>
          <button
            onClick={() => setExpanded(false)}
            style={{
              width: 28,
              height: 28,
              borderRadius: 6,
              border: '1px solid var(--border)',
              background: 'transparent',
              color: 'var(--fg3)',
              fontSize: 16,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            ×
          </button>
        </div>
      </div>

      {/* Instruction banner */}
      <div
        style={{
          padding: '10px 18px',
          background: 'color-mix(in srgb, var(--medium) 6%, transparent)',
          borderBottom: '1px solid color-mix(in srgb, var(--medium) 15%, transparent)',
          fontSize: 11,
          color: 'var(--medium)',
          fontFamily: 'var(--font-mono)',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <span
          style={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            background: 'var(--medium)',
            animation: 'prTabPulse 2s ease-in-out infinite',
          }}
        />
        Mark each PR as Tested after verifying it works
      </div>

      {/* How to test guide */}
      <HowToGuide />

      {/* Content */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {/* Loading */}
        {loading && (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--fg3)', fontSize: 12 }}>
            Fetching merged PRs…
          </div>
        )}

        {/* Error */}
        {error && (
          <div style={{ padding: '14px 18px', color: 'var(--danger)', fontSize: 12 }}>
            Failed to load PRs — {error}
          </div>
        )}

        {/* All done */}
        {!loading && !error && untestedPRs.length === 0 && (
          <div style={{ padding: '48px 18px', textAlign: 'center' }}>
            <div style={{ fontSize: 28, marginBottom: 8, opacity: 0.5 }}>✓</div>
            <div style={{ fontSize: 13, color: 'var(--accent)', fontWeight: 600 }}>
              All PRs tested
            </div>
            <div style={{ fontSize: 11, color: 'var(--fg3)', marginTop: 6, lineHeight: 1.5 }}>
              Every recently merged PR has been verified.
              <br />
              New PRs will appear here after they're merged.
            </div>
          </div>
        )}

        {/* Count banner */}
        {!loading && !error && untestedPRs.length > 0 && (
          <div style={{ padding: '16px 18px 8px' }}>
            <div
              style={{
                padding: '10px 14px',
                borderRadius: 8,
                background: 'color-mix(in srgb, var(--medium) 8%, transparent)',
                border: '1px solid color-mix(in srgb, var(--medium) 25%, transparent)',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
              }}
            >
              <span
                style={{
                  padding: '2px 7px',
                  borderRadius: 5,
                  fontSize: 9,
                  fontWeight: 800,
                  background: 'var(--medium)',
                  color: 'var(--ink-on-bright)',
                  fontFamily: 'var(--font-mono)',
                  letterSpacing: '0.08em',
                }}
              >
                {untestedPRs.length}
              </span>
              <span style={{ fontSize: 12, color: 'var(--medium)', fontWeight: 600 }}>
                PR{untestedPRs.length !== 1 ? 's' : ''} awaiting verification
              </span>
            </div>
          </div>
        )}

        {/* PR list */}
        {!loading &&
          !error &&
          untestedPRs.map((pr) => (
            <PRRow
              key={pr.number}
              pr={pr}
              steps={getStepsForPR(pr.number)}
              howToTestSteps={getHowToTestForPR(pr.number)}
              categories={getCategoriesForPR(pr.number)}
              isStepChecked={isStepChecked}
              onToggleStep={toggleStep}
              onApprove={approve}
              onClaudeFix={() => {}}
            />
          ))}
      </div>
    </div>
  );
}
